<script lang="ts" setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import type { Modifiers } from '../types/condition'
import SancareOcticon from './SancareOcticon.vue'

export interface ConditionValueProps {
  canEdit?: boolean
  canRemove?: boolean
  content: string
  modifiers: Modifiers
}

const props = withDefaults(defineProps<ConditionValueProps>(), {
  canEdit: false,
  canRemove: false,
})

const { t } = useI18n()

const emit = defineEmits<{
  (e: 'edit'): void
  (e: 'remove'): void
}>()

const hasWordDistance = computed(() => props.modifiers?.wordDistance > -1)

function handleEdit(): void {
  emit('edit')
}

function handleRemove(): void {
  emit('remove')
}
</script>

<template>
  <span
    v-if="modifiers.includeAntecedents"
    v-tooltip="{
      content: t('condition.searchInMedicalHistory'),
      placement: 'bottom',
      distance: 5,
    }"
  >
    <sancare-octicon
      name="eye"
      :width="14"
      :height="11"
    />
  </span>
  <span
    v-if="modifiers.includeFamilyAntecedents"
    v-tooltip="{
      content: t('condition.searchInFamilyMedicalHistory'),
      placement: 'bottom',
      distance: 5,
    }"
  >
    <sancare-octicon
      name="organization"
      :width="14"
      :height="11"
    />
  </span>
  <span
    v-if="modifiers && modifiers.includeNegations"
    v-tooltip="{
      content: t('condition.SearchInNegations'),
      placement: 'bottom',
      distance: 5,
    }"
  >
    <sancare-octicon
      name="circle-slash"
      :width="14"
      :height="11"
    />
  </span>
  <span v-if="hasWordDistance">
    [Intervalle: {{ modifiers.wordDistance }}]
  </span>
  <span>{{ content }}</span>
  <span
    v-if="canEdit"
    class="condition-edit mx-1"
    @click="handleEdit"
  >
    <sancare-octicon
      name="pencil"
      :width="14"
      :height="11"
    />
  </span>
  <span
    v-if="canRemove"
    class="condition-remove"
    @click="handleRemove"
  >
    <sancare-octicon
      name="x"
      :width="14"
      :height="11"
    />
  </span>
  <slot name="after-content" />
</template>

<style lang="scss">
.condition-edit,
.condition-remove {
  cursor: pointer;
}
</style>
